/* assets/byob-builder.js
   Drives every BYOB pack page. Reads N (qty) + P (price) from the section.
   Selection is client-side; on confirm it adds the chosen variants to the cart and
   sends the shopper to /cart, where an automatic bundle discount locks the total
   to ₹P. One bundle at a time; CTA enables only when exactly N items are chosen.

   World-class extras:
   • Haptic feedback (navigator.vibrate) on every selection / completion.
   • Subtle pop animation per tap.
   • CSS-driven confetti burst the moment the bundle fills.
   • Optimistic UI + graceful Ajax errors. */
(function () {
  const root = document.querySelector('[data-byob-builder]');
  if (!root) return;

  const N = parseInt(root.dataset.qty, 10) || 6;
  const P = parseInt(root.dataset.price, 10) || 0;
  const PACK_HANDLE = root.dataset.pack || '';
  const sel = new Map(); // variantId -> qty

  /* ----- source / attribution ----- */
  function detectSource() {
    /* 1. Recent stored signal from bridge or hub */
    try {
      const stored = JSON.parse(sessionStorage.getItem('byob_source') || 'null');
      if (stored && stored.at && (Date.now() - stored.at) < 30 * 60 * 1000) {
        return stored;
      }
    } catch (_) {}
    /* 2. UTM / ref params in URL */
    try {
      const params = new URLSearchParams(location.search);
      const utmSource = params.get('utm_source');
      const utmMedium = params.get('utm_medium');
      const utmCampaign = params.get('utm_campaign');
      const ref = params.get('ref');
      if (utmSource || ref) {
        return {
          source: 'campaign',
          utm_source: utmSource || '',
          utm_medium: utmMedium || '',
          utm_campaign: utmCampaign || '',
          ref: ref || '',
          at: Date.now()
        };
      }
    } catch (_) {}
    /* 3. Referrer-derived */
    try {
      if (document.referrer) {
        const r = new URL(document.referrer);
        if (r.host !== location.host) {
          if (/instagram|facebook|fb\.|meta|t\.co|twitter|x\.com|threads|whatsapp/i.test(r.host)) return { source: 'social', host: r.host, at: Date.now() };
          if (/google|bing|duckduck|yahoo/i.test(r.host)) return { source: 'search', host: r.host, at: Date.now() };
          return { source: 'external', host: r.host, at: Date.now() };
        }
        /* same-origin referrer */
        if (/\/products\//.test(r.pathname)) return { source: 'pdp_direct', referrer_path: r.pathname, at: Date.now() };
        if (/\/pages\/byob\b/.test(r.pathname)) return { source: 'hub', at: Date.now() };
        if (r.pathname === '/' || r.pathname === '') return { source: 'home', at: Date.now() };
        return { source: 'internal', referrer_path: r.pathname, at: Date.now() };
      }
    } catch (_) {}
    return { source: 'direct', at: Date.now() };
  }
  const SRC = detectSource();

  /* If we came from a PDP referrer (no stored bridge data) try to recover
     the source product handle from the referrer path so we can still
     auto-select it below. */
  if (!SRC.product_handle && SRC.referrer_path) {
    const m = SRC.referrer_path.match(/\/products\/([^/?#]+)/);
    if (m) SRC.product_handle = m[1];
  }

  function fireEvent(name, payload) {
    try { document.dispatchEvent(new CustomEvent('byob:' + name, { detail: payload || {} })); } catch (_) {}
    if (window.dataLayer && window.dataLayer.push) {
      window.dataLayer.push(Object.assign({ event: 'byob_' + name }, payload || {}));
    }
  }

  fireEvent('builder_view', {
    pack_handle: PACK_HANDLE,
    qty_required: N,
    bundle_price: P,
    source: SRC.source,
    source_detail: SRC
  });

  const sum     = root.querySelector('[data-sum]');
  const countEl = sum.querySelector('[data-count]');
  const dots    = [...sum.querySelectorAll('[data-dot]')];
  const cta     = sum.querySelector('[data-cta]');
  const hint    = sum.querySelector('[data-hint]');
  const wasEl   = sum.querySelector('[data-was]');
  const saveEl  = sum.querySelector('[data-save]');
  const tray    = sum.querySelector('[data-tray]');

  let lastReadyState = false;

  const reducedMotion = () =>
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const total = () => [...sel.values()].reduce((a, b) => a + b, 0);

  function buzz(pattern) {
    if (reducedMotion()) return;
    if (navigator.vibrate) { try { navigator.vibrate(pattern); } catch (_) {} }
  }

  function pop(card) {
    if (!card || reducedMotion()) return;
    card.classList.remove('is-pop');
    void card.offsetWidth;
    card.classList.add('is-pop');
  }

  function confettiBurst() {
    if (reducedMotion()) return;
    const colors = ['#ed1c24', '#121212', '#ffbe2e', '#27c39e', '#f6f0e6'];
    const wrap = document.createElement('div');
    wrap.className = 'byob-confetti';
    for (let i = 0; i < 60; i++) {
      const s = document.createElement('span');
      s.style.left = (Math.random() * 100) + 'vw';
      s.style.background = colors[i % colors.length];
      s.style.animationDelay = (Math.random() * 250) + 'ms';
      s.style.animationDuration = (1100 + Math.random() * 700) + 'ms';
      s.style.transform = `translateY(0) rotate(${Math.random() * 90}deg)`;
      wrap.appendChild(s);
    }
    document.body.appendChild(wrap);
    setTimeout(() => wrap.remove(), 2000);
  }

  function originalTotal() {
    let t = 0;
    sel.forEach((qty, id) => {
      const card = root.querySelector('.byob-prod[data-id="' + id + '"]');
      if (!card) return;
      const price = parseFloat(card.dataset.price) || 0;
      t += price * qty;
    });
    return t;
  }

  function renderTray() {
    if (!tray) return;
    if (sel.size === 0) {
      tray.hidden = true;
      tray.innerHTML = '';
      return;
    }
    tray.hidden = false;
    const frag = document.createDocumentFragment();
    sel.forEach((qty, id) => {
      const card = root.querySelector('.byob-prod[data-id="' + id + '"]');
      if (!card) return;
      const imgEl = card.querySelector('img');
      const src = (imgEl && (imgEl.currentSrc || imgEl.src)) || card.dataset.img || '';
      const title = card.dataset.title || (card.querySelector('.byob-prod__name') || {}).textContent || '';
      const item = document.createElement('div');
      item.className = 'byob-sum__tray-item';
      item.dataset.id = id;
      item.title = title;
      const img = document.createElement('img');
      img.className = 'byob-sum__tray-img';
      img.src = src;
      img.alt = title;
      img.loading = 'lazy';
      item.appendChild(img);
      if (qty > 1) {
        const q = document.createElement('span');
        q.className = 'byob-sum__tray-qty';
        q.textContent = '×' + qty;
        item.appendChild(q);
      }
      const rm = document.createElement('button');
      rm.type = 'button';
      rm.className = 'byob-sum__tray-remove';
      rm.dataset.remove = id;
      rm.setAttribute('aria-label', 'Remove ' + title);
      rm.innerHTML = '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>';
      item.appendChild(rm);
      frag.appendChild(item);
    });
    tray.innerHTML = '';
    tray.appendChild(frag);
  }

  function render() {
    const c = total();
    countEl.textContent = c;
    dots.forEach((d, i) => d.classList.toggle('is-on', i < c));

    /* Fill the box slots — one slot per required item, in pick order. A slot
       is the same element as the old progress dot, so nothing else changed. */
    const picked = [];
    sel.forEach((qty, id) => { for (let k = 0; k < qty; k++) picked.push(id); });
    dots.forEach((slot, i) => {
      const img = slot.querySelector('.byob-slot__img');
      const x = slot.querySelector('[data-slot-remove]');
      if (!img || !x) return;
      const id = picked[i];
      if (!id) {
        slot.classList.add('is-empty'); slot.classList.remove('is-filled');
        img.hidden = true; img.removeAttribute('src'); x.hidden = true;
        slot.removeAttribute('data-id');
        return;
      }
      const card = root.querySelector('.byob-prod[data-id="' + id + '"]');
      const im = card && card.querySelector('img');
      const src = (im && (im.currentSrc || im.src)) || (card && card.dataset.img) || '';
      slot.classList.remove('is-empty'); slot.classList.add('is-filled');
      slot.dataset.id = id;
      if (src && img.getAttribute('src') !== src) img.src = src;
      img.alt = (card && card.dataset.title) || '';
      img.hidden = false; x.hidden = false;
    });

    root.querySelectorAll('.byob-prod').forEach((card) => {
      const id = card.dataset.id;
      const q = sel.get(id) || 0;
      card.classList.toggle('is-sel', q > 0);
      const addBtn = card.querySelector('[data-add]');
      const step = card.querySelector('[data-step]');
      addBtn.hidden = q > 0;
      step.hidden = q === 0;
      if (q > 0) step.querySelector('[data-qn]').textContent = q;
      const incBtn = card.querySelector('[data-inc]');
      if (incBtn) incBtn.disabled = c >= N;
      addBtn.disabled = c >= N;
    });

    /* live original total + savings */
    if (typeof qvSync === 'function') qvSync();

    const orig = originalTotal();
    if (wasEl && saveEl) {
      if (c > 0 && orig > P) {
        const saved = Math.round(orig - P);
        const pctOff = Math.round((saved / orig) * 100);
        wasEl.textContent = 'Rs. ' + Math.round(orig);
        wasEl.hidden = false;
        saveEl.innerHTML = 'You save <strong>Rs. ' + saved + '</strong> (' + pctOff + '% off)';
        saveEl.hidden = false;
      } else {
        wasEl.hidden = true;
        saveEl.hidden = true;
      }
    }

    if (c < N) {
      cta.disabled = true;
      cta.textContent = `Add ${N} to unlock Rs. ${P}`;
      hint.textContent = `Add ${N - c} more`;
    } else {
      cta.disabled = false;
      cta.textContent = `Add bundle to cart · Rs. ${P}`;
      hint.textContent = `You're all set 🎉`;
    }

    const isReady = c >= N;
    sum.classList.toggle('is-ready', isReady);
    if (isReady && !lastReadyState) {
      buzz([12, 60, 30, 80]);
      confettiBurst();
    }
    lastReadyState = isReady;
    renderTray();
  }

  function change(id, delta, card) {
    const c = total();
    if (delta > 0 && c >= N) {
      buzz(8);
      if (card && card.animate) {
        card.animate(
          [
            { transform: 'translateX(0)' },
            { transform: 'translateX(-4px)' },
            { transform: 'translateX(4px)' },
            { transform: 'translateX(0)' }
          ],
          { duration: 240, easing: 'cubic-bezier(.36,.07,.19,.97)' }
        );
      }
      return;
    }
    const next = (sel.get(id) || 0) + delta;
    if (next <= 0) sel.delete(id); else sel.set(id, next);
    if (delta > 0) { buzz(10); pop(card); }
    else { buzz(4); }
    render();
  }

  root.addEventListener('click', (e) => {
    const card = e.target.closest('.byob-prod');
    if (!card) return;
    /* Controls first; anything else on the card opens the shadow PDP. */
    if (e.target.closest('[data-add],[data-inc],[data-dec]')) {
      if (card.dataset.soldOut === 'true') return;
      const id = card.dataset.id;
      if (e.target.closest('[data-inc]') || e.target.closest('[data-add]')) change(id, +1, card);
      else change(id, -1, card);
      return;
    }
    if (dragged) { dragged = false; return; }
    openQV(card);
  });

  /* ═══ SHADOW PDP ═══════════════════════════════════════════════════════════
     Images, description and price without leaving the builder. It drives the
     SAME selection through change(), so there is no second source of truth for
     what is in the bundle. Product data comes from /products/<handle>.js on
     first open and is cached per handle. */
  const qv = document.querySelector('[data-qv]');
  const qvCache = {};
  let qvCard = null;

  function qvMoney(n) { return 'Rs. ' + Math.round(n); }
  function qvPlain(html) {
    const t = document.createElement('textarea');
    t.innerHTML = String(html || '')
      .replace(/<\s*(br|\/p|\/li|\/h[1-6]|\/div)[^>]*>/gi, '\n')
      .replace(/<[^>]+>/g, ' ');
    return t.value.replace(/[ \t\u00a0]+/g, ' ').replace(/ ?\n ?/g, '\n')
            .replace(/\n{3,}/g, '\n\n').trim();
  }

  function qvSync() {
    if (!qv || !qvCard) return;
    const q = sel.get(qvCard.dataset.id) || 0;
    const c = total();
    const step = qv.querySelector('[data-qv-step]');
    const add = qv.querySelector('[data-qv-add]');
    const soldOut = qvCard.dataset.soldOut === 'true';
    step.hidden = q === 0;
    add.hidden = q > 0;
    if (q > 0) qv.querySelector('[data-qv-qn]').textContent = q;
    add.disabled = soldOut || c >= N;
    add.textContent = soldOut ? 'Sold out' : (c >= N ? 'Bundle full' : 'Add to bundle');
    const inc = qv.querySelector('[data-qv-inc]');
    if (inc) inc.disabled = c >= N;
  }

  async function openQV(card) {
    if (!qv) return;
    qvCard = card;
    qv.hidden = false;
    document.body.style.overflow = 'hidden';
    const strip = qv.querySelector('[data-qv-strip]');
    const dots = qv.querySelector('[data-qv-dots]');
    qv.querySelector('[data-qv-title]').textContent = card.dataset.title || '';
    qv.querySelector('[data-qv-price]').textContent = qvMoney(card.dataset.price || 0);
    qv.querySelector('[data-qv-desc]').textContent = '';
    strip.replaceChildren(); dots.replaceChildren();
    /* Show the card's own image immediately so it never opens blank. */
    const thumb = card.querySelector('img');
    if (thumb) {
      const im = document.createElement('img');
      im.src = thumb.currentSrc || thumb.src; im.alt = card.dataset.title || '';
      strip.appendChild(im);
    }
    qvSync();
    qv.querySelector('[data-qv-close]').focus();

    const handle = card.dataset.handle;
    let data = qvCache[handle];
    if (!data && handle) {
      try { data = await (await fetch('/products/' + handle + '.js')).json(); qvCache[handle] = data; }
      catch (err) { return; }
    }
    if (!data || qvCard !== card || qv.hidden) return;   // closed or moved on while fetching

    strip.replaceChildren(); dots.replaceChildren();
    const imgs = (data.images || []).slice(0, 6);
    imgs.forEach((src, i) => {
      const im = document.createElement('img');
      im.src = String(src).split('?')[0] + '?width=900';
      im.alt = data.title; im.loading = i ? 'lazy' : 'eager'; im.decoding = 'async';
      strip.appendChild(im);
      const d = document.createElement('i'); if (!i) d.className = 'on';
      dots.appendChild(d);
    });
    dots.style.display = imgs.length > 1 ? '' : 'none';
    strip.scrollLeft = 0;
    strip.onscroll = () => {
      const i = Math.round(strip.scrollLeft / strip.clientWidth);
      dots.querySelectorAll('i').forEach((d, k) => d.classList.toggle('on', k === i));
    };
    qv.querySelector('[data-qv-desc]').textContent = qvPlain(data.description);
    const cmp = data.compare_at_price && data.compare_at_price > data.price
      ? '<s>' + qvMoney(data.compare_at_price / 100) + '</s>' : '';
    qv.querySelector('[data-qv-price]').innerHTML = qvMoney(data.price / 100) + cmp;
  }

  function closeQV() {
    if (!qv) return;
    qv.hidden = true;
    document.body.style.overflow = '';
    qvCard = null;
  }

  if (qv) {
    qv.querySelector('[data-qv-close]').addEventListener('click', closeQV);
    qv.querySelector('[data-qv-veil]').addEventListener('click', closeQV);
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeQV(); });
    qv.addEventListener('click', (e) => {
      if (!qvCard) return;
      const id = qvCard.dataset.id;
      if (e.target.closest('[data-qv-add]') || e.target.closest('[data-qv-inc]')) change(id, +1, qvCard);
      else if (e.target.closest('[data-qv-dec]')) change(id, -1, qvCard);
      else return;
      qvSync();
    });
  }

  /* ═══ CARD IMAGE SWIPE ═════════════════════════════════════════════════════
     Only the featured image is rendered server-side; the rest are fetched the
     first time a card is touched or hovered, so 50 cards don't ship 150 images.
     A drag must not count as a tap, or swiping would open the shadow PDP. */
  const cardImgCache = {};
  let dragged = false, downX = 0, downY = 0;

  root.addEventListener('pointerdown', (e) => {
    dragged = false; downX = e.clientX; downY = e.clientY;
    const media = e.target.closest('[data-media]');
    if (media) hydrateCard(media.closest('.byob-prod'));
  }, { passive: true });

  root.addEventListener('pointermove', (e) => {
    if (!downX && !downY) return;
    if (Math.abs(e.clientX - downX) > 8 || Math.abs(e.clientY - downY) > 8) dragged = true;
  }, { passive: true });

  async function hydrateCard(card) {
    if (!card || card.dataset.hydrated) return;
    card.dataset.hydrated = '1';
    const handle = card.dataset.handle;
    const slides = card.querySelector('[data-slides]');
    const dotsEl = card.querySelector('[data-cdots]');
    if (!handle || !slides || !dotsEl) return;
    let data = cardImgCache[handle];
    if (!data) {
      try { data = await (await fetch('/products/' + handle + '.js')).json(); cardImgCache[handle] = data; }
      catch (err) { return; }
    }
    const imgs = (data.images || []).slice(1, 5);          // featured is already there
    if (!imgs.length) return;
    imgs.forEach((src) => {
      const im = document.createElement('img');
      im.className = 'byob-prod__img';
      im.src = String(src).split('?')[0] + '?width=400';
      im.alt = data.title; im.loading = 'lazy'; im.decoding = 'async';
      slides.appendChild(im);
    });
    dotsEl.replaceChildren();
    for (let i = 0; i < imgs.length + 1; i++) {
      const d = document.createElement('i'); if (!i) d.className = 'on';
      dotsEl.appendChild(d);
    }
    dotsEl.hidden = false;
    slides.addEventListener('scroll', () => {
      const i = Math.round(slides.scrollLeft / slides.clientWidth);
      dotsEl.querySelectorAll('i').forEach((d, k) => d.classList.toggle('on', k === i));
    }, { passive: true });
  }

  /* Remove straight from a filled slot in the box. */
  root.addEventListener('click', (e) => {
    const rm = e.target.closest('[data-slot-remove]');
    if (!rm) return;
    e.preventDefault(); e.stopPropagation();
    const slot = rm.closest('[data-slot]');
    const id = slot && slot.dataset.id;
    if (!id || !sel.has(id)) return;
    change(id, -1, root.querySelector('.byob-prod[data-id="' + id + '"]'));
  });

  /* Remove-from-tray (X button on each selected thumbnail) */
  if (tray) {
    tray.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-remove]');
      if (!btn) return;
      e.preventDefault();
      e.stopPropagation();
      const id = btn.dataset.remove;
      if (!sel.has(id)) return;
      sel.delete(id);
      buzz(4);
      render();
    });
  }

  /* Auto-select the product the customer was viewing on the PDP.
     We try variant_id first (exact match), then fall back to product handle. */
  function autoSelectFromSource() {
    if (root.dataset.autoSelected === 'true') return false;
    if (sel.size > 0) return false;
    if (!SRC) return false;
    if (!SRC.variant_id && !SRC.product_handle) return false;

    let card = null;
    if (SRC.variant_id) {
      card = root.querySelector('.byob-prod[data-id="' + SRC.variant_id + '"]');
    }
    if (!card && SRC.product_handle) {
      card = root.querySelector('.byob-prod[data-handle="' + SRC.product_handle + '"]');
    }
    if (!card) return false;
    if (card.dataset.soldOut === 'true') return false;

    root.dataset.autoSelected = 'true';
    change(card.dataset.id, +1, card);
    try { card.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (_) {}
    fireEvent('source_autoselected', {
      pack_handle: PACK_HANDLE,
      variant_id: card.dataset.id,
      product_handle: card.dataset.handle || ''
    });
    return true;
  }

  cta.addEventListener('click', async () => {
    if (total() !== N) return;
    cta.disabled = true;
    const original = cta.textContent;
    cta.textContent = 'Adding…';
    buzz([8, 40, 8]);

    /* attribution properties on every line item (visible in Shopify order detail) */
    const at = new Date().toISOString().slice(0, 19).replace('T', ' ');
    const baseProps = {
      _byob_pack: PACK_HANDLE,
      _byob_source: SRC.source || 'direct',
      _byob_at: at
    };
    if (SRC.product_id)    baseProps._byob_from_product_id = String(SRC.product_id);
    if (SRC.product_handle) baseProps._byob_from_product_handle = SRC.product_handle;
    if (SRC.utm_source)    baseProps._byob_utm_source = SRC.utm_source;
    if (SRC.utm_medium)    baseProps._byob_utm_medium = SRC.utm_medium;
    if (SRC.utm_campaign)  baseProps._byob_utm_campaign = SRC.utm_campaign;
    if (SRC.ref)           baseProps._byob_ref = SRC.ref;
    if (SRC.host)          baseProps._byob_referrer_host = SRC.host;

    const items = [...sel.entries()].map(([id, q]) => ({
      id: Number(id),
      quantity: q,
      properties: baseProps
    }));

    fireEvent('bundle_added', {
      pack_handle: PACK_HANDLE,
      qty: N,
      bundle_price: P,
      source: SRC.source,
      source_detail: SRC,
      items: items.map(i => ({ variant_id: i.id, quantity: i.quantity })),
      from_product_id: SRC.product_id || null,
      from_product_handle: SRC.product_handle || null
    });

    /* Native Dawn/Rise pattern:
       1. Find the cart drawer or notification element on the page.
       2. Ask it which sections it wants re-rendered.
       3. POST to /cart/add.js with `sections=` so the response includes that HTML.
       4. Pass the parsed response to cartElement.renderContents() — it re-renders
          the sections and opens the drawer in one call. */
    const cartElement = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    let sectionsParam = '';
    let sectionsSelectors = '';
    if (cartElement && typeof cartElement.getSectionsToRender === 'function') {
      const list = cartElement.getSectionsToRender();
      sectionsParam = list.map((s) => s.id).join(',');
      sectionsSelectors = list
        .filter((s) => s.selector)
        .map((s) => s.id + ':' + s.selector)
        .join(',');
    }

    try {
      const body = { items: items };
      if (sectionsParam) {
        body.sections = sectionsParam;
        body.sections_url = window.location.pathname;
      }
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      });
      if (!res.ok) {
        const errBody = await res.text().catch(() => '');
        throw new Error('cart-add failed: ' + res.status + ' ' + errBody.slice(0, 200));
      }
      const added = await res.json();

      /* clear source so the next bundle on the same session gets a fresh detection */
      try { sessionStorage.removeItem('byob_source'); } catch (_) {}

      cta.textContent = 'Added ✓';

      /* Re-render cart sections + open drawer (Dawn/Rise standard) */
      if (cartElement && typeof cartElement.renderContents === 'function' && added && added.sections) {
        try {
          /* Notify other components first (cart.js listens to this) */
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: 'byob-builder',
              productVariantId: items[0] && items[0].id,
              cartData: added
            });
          }
          cartElement.renderContents(added);
          return;
        } catch (renderErr) {
          /* fall through to the last-resort click */
          console.warn('[byob] cart renderContents failed, falling back', renderErr);
        }
      }

      /* Last resort: click the header cart icon to trigger the drawer the normal way */
      const cartIcon = document.querySelector('#cart-icon-bubble, [data-cart-icon], .header__icon--cart');
      if (cartIcon) { try { cartIcon.click(); return; } catch (_) {} }

      /* If no drawer exists at all in this theme, go to /cart so the bundle isn't lost */
      window.location.href = '/cart';
    } catch (err) {
      cta.disabled = false;
      cta.textContent = 'Try again';
      hint.textContent = "Couldn't add — please try once more.";
      buzz([20, 50, 20]);
      fireEvent('bundle_add_error', { pack_handle: PACK_HANDLE, error: String(err) });
      setTimeout(() => { cta.textContent = original; }, 2200);
    }
  });

  render();
  autoSelectFromSource();

  /* ----- Load remaining pages (server-rendered) -----
     Shopify's `paginate` tag caps at 50 products per page. For larger
     collections we fetch every remaining page via the Section Rendering API
     in parallel and append product cards to the grid. The click handler is
     event-delegated on `root`, so newly inserted cards work automatically. */
  (async function loadAllPages() {
    const grid = root.querySelector('[data-byob-grid]');
    if (!grid) return;
    const totalPages = parseInt(grid.dataset.totalPages, 10) || 1;
    if (totalPages <= 1) return;

    const sectionId = root.dataset.sectionId;
    const pagePath = root.dataset.pageUrl || window.location.pathname;
    if (!sectionId) return;

    const seen = new Set(
      [...grid.querySelectorAll('.byob-prod')].map((el) => el.dataset.id)
    );

    const fetchPage = async (p) => {
      try {
        const url = `${pagePath}?section_id=${encodeURIComponent(sectionId)}&page=${p}`;
        const res = await fetch(url, { headers: { 'Accept': 'text/html' } });
        if (!res.ok) throw new Error('status ' + res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return [...doc.querySelectorAll('.byob-prod')];
      } catch (err) {
        console.warn('[byob] failed to load page ' + p, err);
        return [];
      }
    };

    const requests = [];
    for (let p = 2; p <= totalPages; p++) requests.push(fetchPage(p));
    const pages = await Promise.all(requests);

    const frag = document.createDocumentFragment();
    pages.forEach((cards) => {
      cards.forEach((card) => {
        const id = card.dataset.id;
        if (!id || seen.has(id)) return;
        seen.add(id);
        frag.appendChild(card);
      });
    });
    if (frag.childNodes.length) {
      grid.appendChild(frag);
      /* Source product may have been on a later page — try once more. */
      autoSelectFromSource();
    }
  })();
})();
